import { z } from 'zod'
import { PaginatedResponseSchema } from '@/types/common/PaginatedResponse'

/**
 * The connected applications of an account: GET /account/grants and
 * GET /account/grants/:grant_id/activity. Shapes mirror
 * backend/internal/http/api/oauth_grants.go.
 */

export const OauthGrantClient = z.object({
  name: z.string(),
  /** Absent for a dynamically registered client, whose name is only what it typed. */
  host: z.string().nullish()
})

export const OauthGrantStatus = z.enum(['active', 'revoked', 'expired']).catch('revoked')

/** Why a grant ended. A reason this build does not know reads as none. */
export const OauthGrantRevokedReason = z.enum([
  'customer',
  'code_replay',
  'refresh_reuse',
  'client_removed',
  'admin',
  'expired',
  'account_closed',
  'agent',
  'password_changed'
])

export const OauthGrant = z.object({
  id: z.number(),
  client: OauthGrantClient,
  status: OauthGrantStatus,
  revoked_reason: OauthGrantRevokedReason.nullish().catch(null),
  scopes: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  spend_cap_cents: z.number(),
  spent_cents: z.number(),
  currency: z.string(),
  consented_at: z.string(),
  created_at: z.string(),
  last_used_at: z.string().nullish(),
  expires_at: z.string().nullish(),
  revoked_at: z.string().nullish()
})

export const OauthGrantDetails = OauthGrant.extend({
  consent_ip: z.string(),
  consent_user_agent: z.string(),
  consent_version: z.string().optional(),
  transfer_consent: z.boolean().default(false)
})

export const PaginatedOauthGrants = PaginatedResponseSchema(OauthGrantDetails)

/**
 * How a call row ended. A row is written as attempted before the request runs;
 * unknown means it was cut off or never answered, so whether it changed
 * anything is not known. An outcome this build does not know reads the same.
 */
export const OauthCallOutcome = z.enum(['attempted', 'ok', 'client_error', 'server_error', 'denied', 'unknown'])

/** Why a refused row was refused. A reason this build does not know reads as none. */
export const OauthRefusalReason = z.enum(['scope', 'session_only', 'unmapped', 'admin', 'audience', 'agent_access'])

export const OauthGrantActivity = z.object({
  id: z.number(),
  kind: z.enum(['call', 'refused']),
  tool: z.string().nullish(),
  method: z.string(),
  route: z.string(),
  path: z.string().nullish(),
  reason: OauthRefusalReason.nullish().catch(null),
  required_scopes: z.array(z.string()).nullish(),
  scopes_used: z.array(z.string()).nullish(),
  api_audience: z.boolean().nullish(),
  /**
   * The arguments the tool keeps (scalars and lists of strings), the values it
   * recorded beside them as `previous_*`, and `_truncated` when anything was
   * cut to fit. Written by the application, so rendered as text only.
   */
  args: z.record(z.string(), z.unknown()).nullish(),
  /** 0 until the request has answered. */
  status: z.number(),
  outcome: OauthCallOutcome.catch('unknown'),
  duration_ms: z.number(),
  client_ip: z.string(),
  client_name: z.string().nullish(),
  created_at: z.string()
})

export const PaginatedOauthGrantActivity = PaginatedResponseSchema(OauthGrantActivity)

export type OauthGrant = z.infer<typeof OauthGrant>
export type OauthGrantDetails = z.infer<typeof OauthGrantDetails>
export type PaginatedOauthGrants = z.infer<typeof PaginatedOauthGrants>
export type OauthGrantActivity = z.infer<typeof OauthGrantActivity>
export type OauthGrantRevokedReason = z.infer<typeof OauthGrantRevokedReason>
export type OauthCallOutcome = z.infer<typeof OauthCallOutcome>
export type OauthRefusalReason = z.infer<typeof OauthRefusalReason>
export type PaginatedOauthGrantActivity = z.infer<typeof PaginatedOauthGrantActivity>
